// WF-FIX (2026-10-26; Codex audit "Live is derived from active status, not health"): whether a
// live DE is actually doing anything. "Live" now needs real activity — its newest run, or an MCP
// call with one of its keys (a self-hosted Hermes DE polls its inbox through MCP) — within the
// last day; an active DE with none says "Idle" and why. Every other readiness (draft, deploying,
// paused…) is the server's, unchanged.
import { Badge } from "@companyos/ui";
import type { DeProfile } from "../../api/types";
import { formatDateTime } from "../format";
import { ReadinessBadge } from "./readiness";

/** How recent the last run or MCP call must be for "Live". */
export const LIVE_WINDOW_MS = 24 * 3_600_000;

type ActivityDe = Pick<DeProfile, "readiness" | "keys"> & {
  activity?: { last_run_at: string | null; last_mcp_at: string | null } | undefined;
};

/** The newest run or MCP call (any of its keys) we know of, or null. */
export function lastSeen(de: ActivityDe): string | null {
  const times = [
    de.activity?.last_run_at ?? null,
    de.activity?.last_mcp_at ?? null,
    ...de.keys.map((k) => k.last_used_at ?? null),
  ].filter((t): t is string => Boolean(t));
  return times.sort((a, b) => Date.parse(a) - Date.parse(b)).at(-1) ?? null;
}

export type Liveness =
  | { state: "live"; label: "Live"; reason: string }
  | { state: "idle"; label: "Idle"; reason: string };

/** For a DE the server calls live: Live with recent activity, else Idle; null otherwise. */
export function liveness(de: ActivityDe, now = Date.now()): Liveness | null {
  if (de.readiness.state !== "live") return null;
  const seen = lastSeen(de);
  if (seen && now - Date.parse(seen) <= LIVE_WINDOW_MS)
    return { state: "live", label: "Live", reason: `Last run or MCP call ${formatDateTime(seen)}` };
  return {
    state: "idle",
    label: "Idle",
    reason: seen
      ? `Active, but no run or MCP call since ${formatDateTime(seen)}`
      : "Active, but no run or MCP call recorded yet",
  };
}

/** The profile header's readiness chip, with Live earned by activity. */
export function DeLiveBadge({ de, className }: { de: ActivityDe; className?: string }) {
  const l = liveness(de);
  if (!l) return <ReadinessBadge readiness={de.readiness} {...(className ? { className } : {})} />;
  return (
    <Badge
      variant={l.state === "live" ? "success" : "warning"}
      dot
      data-readiness={l.state}
      title={l.reason}
      className={className}
    >
      {l.label}
      <span className="sr-only">: {l.reason}</span>
    </Badge>
  );
}
